import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Divider,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Link,
  SvgIcon,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { useParams } from "react-router-dom";
import useCommonGetInit from "../../events/hooks/useCommonGetInit";
import MuPb from "../../../widgets/MuPb";
import { getErrorResponse } from "../../../utils2024/utils";
import BackBtn from "../admin/widgets/BackBtn";
import useGetEventSettings from "../../hooks/useGetEventSettings";

// Styled components
const BannerContainer = styled(Box)(({ theme, banner }) => ({
  position: "relative",
  height: 200,
  backgroundColor: theme.palette.grey[300],
  backgroundImage: banner ? `url(${banner})` : "none",
  backgroundSize: "cover",
  backgroundPosition: "center",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  marginTop: -50,
  marginLeft: theme.spacing(2),
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  border: `4px solid ${theme.palette.background.paper}`,
}));

const XIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
  </SvgIcon>
);

const VisitorProfileDetail = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error } = useCommonGetInit(
    `user/get-user-by-id?id=${id}`
  );
  const {
    data: eD,
    isLoading: isEdLoading,
    isError: isEdError,
    error: edError,
  } = useGetEventSettings();

  if (isLoading || isEdLoading) {
    return <MuPb />;
  }

  if (isError || !data) {
    return (
      <Alert severity="error">
        {error ? getErrorResponse(error) : "Failed to load visitor data"}
      </Alert>
    );
  }

  const socialLinks = [
    { key: "in", icon: <InstagramIcon />, label: "Instagram", url: data.in },
    { key: "x", icon: <XIcon />, label: "X (Twitter)", url: data.x },
    { key: "fb", icon: <FacebookIcon />, label: "Facebook", url: data.fb },
    { key: "ln", icon: <LinkedInIcon />, label: "LinkedIn", url: data.ln },
  ];

  return (
    <Box>
      <BackBtn />
      <Box sx={{ maxWidth: 800, mx: "auto", my: 2 }}>
        <Card elevation={3}>
          <BannerContainer
            banner={data?.profileBanner || eD?.defProfileBanner}
          />
          <ProfileImageContainer>
            <ProfileAvatar src={data.profileImage} alt={data.firstName}>
              {data.firstName?.charAt(0)}
            </ProfileAvatar>
          </ProfileImageContainer>

          <CardContent>
            {/* Name + Designation */}
            <Typography variant="h5" component="div" sx={{ mb: 1 }}>
              {data.title} {data.firstName}
            </Typography>
            {data.designation && (
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {data.designation}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Details Grid */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1">{data.username}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{data.email}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mobile No
                </Typography>
                <Typography variant="body1">{data.mobileNo}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company Name
                </Typography>
                <Typography variant="body1">{data.companyName}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Industry Sector
                </Typography>
                <Typography variant="body1">{data.industrySector}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Department
                </Typography>
                <Typography variant="body1">{data.department}</Typography>
              </Grid>

              <Grid item xs={12} sm={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body1">
                  {data.address}, {data.city?.name}, {data.state?.name},{" "}
                  {data.country?.name}, {data.pinzip}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  How did you find out about this event?
                </Typography>
                <Typography variant="body1">
                  {data.howDidYouFindOutAboutThisEvent}
                </Typography>
              </Grid>

              {/* Product Categories */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Product Categories
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {data?.selectProductCategoriesChooseMin10Products?.map(
                    (category, index) => (
                      <Chip
                        key={index}
                        label={category.SubCategory}
                        variant="outlined"
                      />
                    )
                  )}
                </Box>
              </Grid>

              {/* Social Media Links */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Social Media
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {socialLinks.map(({ key, icon, label, url }) =>
                    url ? (
                      <Tooltip key={key} title={label}>
                        <IconButton
                          component={Link}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                          size="small"
                        >
                          {icon}
                        </IconButton>
                      </Tooltip>
                    ) : null
                  )}
                </Box>
              </Grid>

              {/* QR Code */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  QR Code
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <img
                    src={data.qrLink}
                    alt="QR Code"
                    style={{ maxWidth: 150, height: "auto" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VisitorProfileDetail;
